/**
 * ProgramShell model — V98-3-2 (ADR-0048, PRD G-1). Pure: no React.
 *
 * Turns a program card from GET /programs (stage derived per period by the
 * server) into the StageRail stations, the default open tab and the
 * VerdictBand counts. Rules (cert-program-ux rounds 3–5):
 *   - ONE filled cell = the open tab; period presence is a pin, never a
 *     second fill. Each active period is pinned to the station it sits in
 *     (a certified period sits in Operate — Report runs alongside it).
 *   - Stations a program never passes through are N/A with a reason.
 *   - Counts come from the shared counter (card.counts) and are scoped.
 */
import { getProgramManifest, type ConformityStage, type ProgramCard, type ProgramManifest, type ProgramTodayCard } from '@aigovops/core';
import { conformityStation } from './ai-program-model';
import { programTitle, type NavCertStage } from './nav-config';

export type CertStage = NavCertStage;
export const CERT_TABS: readonly CertStage[] = ['scope', 'prepare', 'audit', 'operate', 'report'];
export function isCertTab(v: unknown): v is CertStage {
  return typeof v === 'string' && (CERT_TABS as readonly string[]).includes(v);
}

export type StationState = 'done' | 'active' | 'upcoming' | 'na';
export interface StationModel {
  stage: CertStage;
  state: StationState;
  meta?: string;
  naReason?: string;
  periods: string[];
}

/**
 * A program key or its manifest (ADR-0050). Production passes the key; a
 * manifest can be passed directly so a program the registry doesn't hold yet
 * (the conformance test's fake program) renders with no code change.
 */
export type ProgramRef = string | ProgramManifest;
export function resolveProgram(p: ProgramRef): ProgramManifest | undefined {
  return typeof p === 'string' ? getProgramManifest(p) : p;
}

/**
 * V102-8-1: the manifest a program route opens. An active manifest always
 * opens (absent → its "Add program" state). A planned manifest (CMMC) opens
 * only when GET /programs returned its card — the server lists it only under
 * the staging preview, so the server stays the authority; no client flag or
 * URL parameter can open it. `'pending'` while the cards are still loading.
 */
export function routeManifest(key: string | undefined, cards: ReadonlyArray<{ program: string }> | undefined): ProgramManifest | 'pending' | undefined {
  const m = key ? getProgramManifest(key) : undefined;
  if (!m) return undefined;
  if (m.lifecycle === 'active') return m;
  if (cards === undefined) return 'pending';
  return cards.some((c) => c.program === m.key) ? m : undefined;
}

/** The panel id whose station is derived from the EU risk tiers (AI Regulatory's Conformity). */
export const CONFORMITY_PANEL = 'ai-conformity';

const TAB_TITLE: Record<CertStage, string> = { scope: 'Scope', prepare: 'Prepare', audit: 'Audit', operate: 'Operate', report: 'Report' };
/** Station title: the manifest's `shell.titles` override (AI Regulatory Audit → "Conformity"), else the default. */
export function stationTitle(program: ProgramRef | null, tab: CertStage): string {
  return (program ? resolveProgram(program)?.shell.titles[tab] : undefined) ?? TAB_TITLE[tab];
}

const PHASE_LABEL: Record<string, string> = {
  scoping: 'Scoping',
  readiness: 'Readiness',
  scheduled: 'Scheduled',
  observation: 'Observation window',
  awaiting_fieldwork: 'Awaiting fieldwork',
  fieldwork: 'Fieldwork',
  certified: 'Certified',
  superseded: 'Superseded',
};

const fmt = (d: string) => {
  const x = new Date(`${d.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(x.getTime()) ? d : x.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};
export const fmtProgramDate = fmt;

type Period = ProgramCard['periods'][number];

/** The station a period sits in (certified → Operate; Report runs alongside). */
export function stationOf(p: Pick<Period, 'stage'>): CertStage {
  return p.stage === 'certified' ? 'operate' : p.stage;
}

const RANK: Record<Period['stage'], number> = { scope: 0, prepare: 1, audit: 2, certified: 3 };
const STATUS_RANK = { on_track: 0, at_risk: 1, off_track: 2 } as const;

/**
 * StageRail stations for a program (absent → every station upcoming).
 * Stations a program never passes through come from the manifest's
 * `shell.notApplicable` (C19 needs the reason). A station whose panel is the
 * Conformity panel (AI Regulatory's Audit slot) is derived from the EU risk
 * tiers (`extra.conformity` from the AI grid): N/A with the reason unless a
 * system we provide is EU high-risk (or a practice is prohibited). A
 * standing program (`stage.model === 'standing'`) has no periods.
 */
export function deriveStations(program: ProgramRef, card: ProgramCard | null, extra: { conformity?: ConformityStage | null } = {}): StationModel[] {
  const m = resolveProgram(program);
  const na = m?.shell.notApplicable ?? {};
  const standing = m?.stage.model === 'standing';
  const periods = card?.periods ?? [];
  const active = periods.filter((p) => p.active);
  const reached = periods.reduce((m, p) => Math.max(m, RANK[p.stage]), -1);
  const pinned = (stage: CertStage) => active.filter((p) => p.period_id !== null && stationOf(p) === stage).map((p) => p.label);
  const here = (stage: CertStage) => active.filter((p) => stationOf(p) === stage);
  const metaFor = (stage: CertStage) => {
    const ps = here(stage);
    if (!ps.length) return undefined;
    const p = ps[0]!;
    // V109-5 built-UI (Medium "Stage rail"): never "Certified" on a station still being prepared or audited.
    const phase = p.phase === 'certified' && stationOf(p) !== 'operate' ? PHASE_LABEL.readiness! : PHASE_LABEL[p.phase] ?? p.phase;
    if (!p.window || (p.phase === 'certified' && stationOf(p) === 'operate')) return phase;
    // R3-U2: a window in fieldwork has already ENDED — its dates are the observation window the auditor
    // tests, not the fieldwork. Say when fieldwork closes (the card's next date for this period), else
    // when the observed window ended.
    if (p.phase === 'fieldwork' || p.phase === 'awaiting_fieldwork') {
      const nd = card?.next_date;
      if (nd && nd.kind === 'period_closes' && nd.ref === p.period_id) return `${phase} · closes ${fmt(nd.date)}`;
      return `${phase} · window ended ${fmt(p.window.end)}`;
    }
    // U5: one date story — an upcoming window says when it OPENS (as the card's next date does), an open one when it ends.
    const today = new Date().toISOString().slice(0, 10);
    return p.window.start.slice(0, 10) > today ? `${phase} · opens ${fmt(p.window.start)}` : `${phase} · ends ${fmt(p.window.end)}`;
  };

  return CERT_TABS.map((stage): StationModel => {
    const naReason = na[stage];
    if (naReason) {
      // AI Regulatory: Operate is the only loop stage it has (obligations in force).
      return { stage, state: 'na', naReason, periods: [] };
    }
    if (m?.shell.panels[stage] === CONFORMITY_PANEL) {
      const c = conformityStation(card ? extra.conformity ?? null : null);
      return c.state === 'na' ? { stage, state: 'na', naReason: c.naReason, periods: [] } : { stage, state: 'active', meta: c.meta, periods: [] };
    }
    if (!card) return { stage, state: 'upcoming', periods: [] };
    if (standing) {
      if (stage === 'scope') return { stage, state: 'active', meta: 'Standing', periods: [] };
      const inForce = active.some((p) => p.concurrent.includes('operate'));
      return { stage, state: inForce ? 'active' : 'upcoming', meta: inForce ? m?.shell.loopLabel ?? 'In force' : undefined, periods: [] };
    }
    const there = here(stage);
    const base = { stage, periods: pinned(stage), meta: metaFor(stage) };
    if (stage === 'report') {
      const running = active.some((p) => p.concurrent.includes('report'));
      return { stage, state: running ? 'active' : 'upcoming', periods: [] };
    }
    if (there.length) return { ...base, state: 'active' };
    const r = stage === 'operate' ? 3 : RANK[stage];
    if (stage !== 'operate' && reached > r) {
      // R3-U3: a done station says WHICH periods are past it ("Done · 2025 · 2026"), so the Prepare cell
      // names the period its tab body (readiness for the open window) belongs to.
      const past = active.filter((p) => RANK[p.stage] > r).map((p) => p.label);
      return { stage, state: 'done', meta: past.length ? `Done · ${past.join(' · ')}` : 'Done', periods: [] };
    }
    return { stage, state: 'upcoming', periods: [] };
  });
}

/**
 * The tab to open by default: the manifest's fixed `shell.defaultTab` (AI
 * Regulatory → Scope), else where the program's worst active period sits.
 */
export function defaultTab(program: ProgramRef, card: ProgramCard | null): CertStage {
  const fixed = resolveProgram(program)?.shell.defaultTab;
  if (fixed && fixed !== 'stage') return fixed;
  if (!card) return 'scope';
  const active = card.periods.filter((p) => p.active);
  if (!active.length) return 'scope';
  const worst = active.reduce((a, b) =>
    STATUS_RANK[b.verdict?.status ?? 'on_track'] > STATUS_RANK[a.verdict?.status ?? 'on_track'] ? b : a);
  return stationOf(worst);
}

export interface VerdictCountModel {
  scope: string;
  value: number;
  noun?: string;
  emphasis?: 'bad';
  href?: string;
}

/**
 * Scoped counts for the VerdictBand, from the shared counter. One red at
 * most (overdue), never repeated across counts. V98-4 review T4-H1: on the
 * program's own page the program's open / overdue are the same for every
 * viewer (`program_open` / `program_overdue`); only "Yours" is personal. The
 * page names the program, so the band doesn't repeat it ("All").
 */
export function verdictCounts(card: ProgramCard, prefix: string): VerdictCountModel[] {
  const c = card.counts;
  // V98-5 review T5-B5: the program band counts only this program's share of yours ("Yours here"), not the Today badge.
  const open = card.program_open ?? c.open;
  const overdue = card.program_overdue ?? c.overdue;
  const out: VerdictCountModel[] = [{ scope: 'Yours here', value: c.yours, noun: 'owed', href: `${prefix}/today` }];
  if (open !== null && open !== undefined) out.push({ scope: 'All', value: open, noun: 'open' });
  if (overdue !== null && overdue !== undefined && overdue > 0) out.push({ scope: 'All', value: overdue, noun: 'overdue', emphasis: 'bad' });
  if (c.shared !== null && c.shared > 0) out.push({ scope: 'Shared', value: c.shared, noun: 'also in another program' });
  return out;
}

/**
 * V106-1-8 / V106-5-6: the band on a SOC 2 / ISO 42001 page reads the same server card as Today — open and not-met
 * points, and the viewer's owed items only when there are some. Zero counts are dropped.
 */
export function objectiveBandCounts(oc: ProgramTodayCard, card: ProgramCard, prefix: string): VerdictCountModel[] {
  const out: VerdictCountModel[] = [];
  const r = oc.readiness;
  const unit = oc.program === 'soc2' ? 'points' : 'objectives';
  if (r && r.total - r.covered > 0) out.push({ scope: 'All', value: r.total - r.covered, noun: `${unit} open` });
  if (r && r.counts.not_met > 0) out.push({ scope: 'All', value: r.counts.not_met, noun: 'not met', emphasis: 'bad' });
  if (oc.late) out.unshift({ scope: oc.late.label, value: Math.max(0, oc.late.days), noun: 'days late', emphasis: 'bad' });
  if (card.counts.yours) out.push({ scope: 'Yours here', value: card.counts.yours, noun: 'owed', href: `${prefix}/today` });
  // The band needs at least one count (contract C20): with nothing open, late or owed, the generic counts stand.
  return out.length ? out : verdictCounts(card, prefix);
}

/** "Type II", "Class B · 20x", target date — derived facts for the header. */
export function programFacts(card: ProgramCard | null): string[] {
  if (!card) return [];
  const f: string[] = [];
  // SOC 2's report type is already in the title (programTitle).
  if (card.certification_class) f.push(`Class ${card.certification_class}`);
  if (card.path) f.push(card.path === 'rev5' ? 'Rev 5' : '20x');
  if (card.target_date) f.push(`Target ${fmt(card.target_date)}`);
  return f;
}

export { programTitle };

// ---- readiness heroes (R3-U14, R3-U15) ------------------------------------------------------

/**
 * R3-U14: KSI hero numbers that add up — met + partial + gaps = total. A
 * requirement with no mapped control is a gap too (nothing proves it), so
 * gaps = gap + unmapped.
 */
export function readinessSplit(c: { met: number; partial: number; gap: number; unmapped?: number; total: number }): { met: number; partial: number; gaps: number; total: number } {
  const gaps = Math.max(0, c.total - c.met - c.partial);
  return { met: c.met, partial: c.partial, gaps, total: c.met + c.partial + gaps };
}

/**
 * R3-U14 / R3-U15: the hero's delta and pace — how many are left to meet and
 * the weekly pace that finishes them by the date that matters (the window
 * opening, or its end). Null when nothing is left.
 */
export function readinessPace(remaining: number, daysLeft: number | null, noun: string | [string, string], by?: string | null): { delta: string; pace: string | null } | null {
  if (remaining <= 0) return null;
  const [one, many] = Array.isArray(noun) ? noun : [noun, `${noun}s`];
  const delta = `${remaining} ${remaining === 1 ? one : many} to go`;
  if (daysLeft === null || daysLeft <= 0) return { delta, pace: null };
  const weeks = Math.max(1, Math.ceil(daysLeft / 7));
  const perWeek = Math.ceil(remaining / weeks);
  return { delta, pace: `~${perWeek} a week${by ? ` to be ready by ${fmt(by)}` : ''}` };
}
